import { Point } from '../geometry';

/**
 * 2D transformation matrix [a, b, c, d, e, f]
 * Represents an affine transformation:
 * | a  c  e |
 * | b  d  f |
 * | 0  0  1 |
 *
 * SSR-compatible - pure math
 */
export type Matrix2D = [number, number, number, number, number, number];

/**
 * Result of the QR decomposition of a 2D transformation matrix
 */
export interface QrDecomposeResult {
  /** Rotation angle in degrees */
  angle: number;
  /** Horizontal scale */
  scaleX: number;
  /** Vertical scale */
  scaleY: number;
  /** Skew along the X axis in degrees */
  skewX: number;
  /** Skew along the Y axis in degrees */
  skewY: number;
  /** Translation along X */
  translateX: number;
  /** Translation along Y */
  translateY: number;
}

/**
 * Options for computing the dimensions matrix
 */
export interface DimensionsMatrixOptions {
  scaleX?: number;
  scaleY?: number;
  flipX?: boolean;
  flipY?: boolean;
  skewX?: number;
  skewY?: number;
}

/**
 * Identity matrix
 */
export const IDENTITY_MATRIX: Matrix2D = [1, 0, 0, 1, 0, 0];

/**
 * Converts degrees to radians
 * @param degrees - Degrees
 * @returns Radians
 */
export function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Converts radians to degrees
 * @param radians - Radians
 * @returns Degrees
 */
export function radiansToDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

/**
 * Creates a rotation matrix
 * @param angle - Angle in degrees
 * @returns Rotation matrix
 */
export function createRotateMatrix(angle: number): Matrix2D {
  const radians = degreesToRadians(angle);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return [cos, sin, -sin, cos, 0, 0];
}

/**
 * Creates a translation matrix
 * @param x - Offset along X
 * @param y - Offset along Y
 * @returns Translation matrix
 */
export function createTranslateMatrix(x: number, y: number): Matrix2D {
  return [1, 0, 0, 1, x, y];
}

/**
 * Creates a scale matrix
 * @param scaleX - Scale along X
 * @param scaleY - Scale along Y
 * @returns Scale matrix
 */
export function createScaleMatrix(scaleX: number, scaleY: number): Matrix2D {
  return [scaleX, 0, 0, scaleY, 0, 0];
}

/**
 * Creates a skew matrix
 * @param skewX - Skew along X in degrees
 * @param skewY - Skew along Y in degrees
 * @returns Skew matrix
 */
export function createSkewMatrix(skewX: number, skewY: number): Matrix2D {
  const skewXRad = degreesToRadians(skewX);
  const skewYRad = degreesToRadians(skewY);
  return [1, Math.tan(skewYRad), Math.tan(skewXRad), 1, 0, 0];
}

/**
 * Computes the transformation matrix that affects object dimensions
 * Used to calculate the selection box bounds
 */
export function calcDimensionsMatrix(options: DimensionsMatrixOptions = {}): Matrix2D {
  const { scaleX = 1, scaleY = 1, flipX = false, flipY = false, skewX = 0, skewY = 0 } = options;

  let matrix = createScaleMatrix(flipX ? -scaleX : scaleX, flipY ? -scaleY : scaleY);

  if (skewX !== 0 || skewY !== 0) {
    matrix = multiplyMatrices(matrix, createSkewMatrix(skewX, skewY));
  }

  return matrix;
}

/**
 * Multiplies two matrices
 * @param a - First matrix
 * @param b - Second matrix
 * @returns Resulting matrix
 */
export function multiplyMatrices(a: Matrix2D, b: Matrix2D): Matrix2D {
  const [a1, a2, a3, a4, a5, a6] = a;
  const [b1, b2, b3, b4, b5, b6] = b;

  return [
    a1 * b1 + a3 * b2,
    a2 * b1 + a4 * b2,
    a1 * b3 + a3 * b4,
    a2 * b3 + a4 * b4,
    a1 * b5 + a3 * b6 + a5,
    a2 * b5 + a4 * b6 + a6,
  ];
}

/**
 * Applies a transformation matrix to a point
 * @param point - Point to transform
 * @param matrix - Transformation matrix
 * @param ignoreOffset - Ignore the offset (e, f in the matrix)
 * @returns Transformed point
 */
export function transformPoint(point: Point, matrix: Matrix2D, ignoreOffset = false): Point {
  const [a, b, c, d, e, f] = matrix;
  return new Point(
    a * point.x + c * point.y + (ignoreOffset ? 0 : e),
    b * point.x + d * point.y + (ignoreOffset ? 0 : f)
  );
}

/**
 * Computes the determinant of a 2x2 matrix
 * @param matrix - Matrix
 * @returns Determinant
 */
function determinant(matrix: Matrix2D): number {
  const [a, b, c, d] = matrix;
  return a * d - b * c;
}

/**
 * Inverts a transformation matrix
 * @param matrix - Matrix to invert
 * @returns Inverted matrix
 */
export function invertMatrix(matrix: Matrix2D): Matrix2D {
  const [a, b, c, d, e, f] = matrix;
  const det = determinant(matrix);

  if (det === 0) {
    // Matrix has no inverse, return the identity
    return [...IDENTITY_MATRIX];
  }

  const invDet = 1 / det;
  return [d * invDet, -b * invDet, -c * invDet, a * invDet, (c * f - d * e) * invDet, (b * e - a * f) * invDet];
}

/**
 * Parameters for creating a composite matrix
 */
export interface ComposeMatrixOptions {
  /** Rotation angle in degrees */
  angle?: number;
  /** Translation along X */
  translateX?: number;
  /** Translation along Y */
  translateY?: number;
  /** Scale along X */
  scaleX?: number;
  /** Scale along Y */
  scaleY?: number;
  /** Skew along X in degrees */
  skewX?: number;
  /** Skew along Y in degrees */
  skewY?: number;
  /** Horizontal flip */
  flipX?: boolean;
  /** Vertical flip */
  flipY?: boolean;
}

/**
 * Creates a composite transformation matrix from parameters
 * Applies transformations in the order: scale -> skew -> rotate -> translate
 *
 * @param options - Transformation parameters
 * @returns Composite matrix
 */
export function composeMatrix(options: ComposeMatrixOptions = {}): Matrix2D {
  const {
    angle = 0,
    translateX = 0,
    translateY = 0,
    scaleX = 1,
    scaleY = 1,
    skewX = 0,
    skewY = 0,
    flipX = false,
    flipY = false,
  } = options;

  let matrix: Matrix2D = [...IDENTITY_MATRIX];

  // Apply flip (as a negative scale)
  const finalScaleX = flipX ? -scaleX : scaleX;
  const finalScaleY = flipY ? -scaleY : scaleY;

  // 1. Scale
  if (finalScaleX !== 1 || finalScaleY !== 1) {
    matrix = multiplyMatrices(matrix, createScaleMatrix(finalScaleX, finalScaleY));
  }

  // 2. Skew
  if (skewX !== 0 || skewY !== 0) {
    matrix = multiplyMatrices(matrix, createSkewMatrix(skewX, skewY));
  }

  // 3. Rotate
  if (angle !== 0) {
    matrix = multiplyMatrices(matrix, createRotateMatrix(angle));
  }

  // 4. Translate
  if (translateX !== 0 || translateY !== 0) {
    matrix = multiplyMatrices(matrix, createTranslateMatrix(translateX, translateY));
  }

  return matrix;
}

/**
 * Performs QR decomposition of a transformation matrix
 * Returns angles, scale and translations
 */
export function qrDecompose(matrix: Matrix2D): QrDecomposeResult {
  const angle = Math.atan2(matrix[1], matrix[0]);
  const denom = matrix[0] * matrix[0] + matrix[1] * matrix[1];
  const scaleX = Math.sqrt(denom);
  const scaleY = (matrix[0] * matrix[3] - matrix[2] * matrix[1]) / (scaleX || 1);
  const skewX = Math.atan2(matrix[0] * matrix[2] + matrix[1] * matrix[3], denom || 1);

  return {
    angle: radiansToDegrees(angle),
    scaleX,
    scaleY,
    skewX: radiansToDegrees(skewX),
    skewY: 0,
    translateX: matrix[4] || 0,
    translateY: matrix[5] || 0,
  };
}

/**
 * Extracts the rotation angle from a transformation matrix
 * @param matrix - Transformation matrix
 * @returns Angle in degrees
 */
export function extractRotation(matrix: Matrix2D): number {
  const [a, b] = matrix;
  const radians = Math.atan2(b, a);
  return radiansToDegrees(radians);
}

/**
 * Extracts the scale from a transformation matrix
 * @param matrix - Transformation matrix
 * @returns Object with scaleX and scaleY
 */
export function extractScale(matrix: Matrix2D): { scaleX: number; scaleY: number } {
  const [a, b, c, d] = matrix;
  const scaleX = Math.sqrt(a * a + b * b);
  const scaleY = Math.sqrt(c * c + d * d);
  return { scaleX, scaleY };
}

/**
 * Extracts the translation from a transformation matrix
 * @param matrix - Transformation matrix
 * @returns Point with the translation coordinates
 */
export function extractTranslation(matrix: Matrix2D): Point {
  const [, , , , e, f] = matrix;
  return new Point(e, f);
}

/**
 * Decomposes a matrix into its transformation components
 * @param matrix - Transformation matrix
 * @returns Object with transformation parameters
 */
export function decomposeMatrix(matrix: Matrix2D): ComposeMatrixOptions {
  const angle = extractRotation(matrix);
  const { scaleX, scaleY } = extractScale(matrix);
  const translation = extractTranslation(matrix);

  return {
    angle,
    scaleX,
    scaleY,
    translateX: translation.x,
    translateY: translation.y,
  };
}

/**
 * Checks whether a matrix is the identity matrix
 * @param matrix - Matrix to check
 * @param epsilon - Allowed tolerance
 * @returns true if the matrix is the identity
 */
export function isIdentityMatrix(matrix: Matrix2D, epsilon = 1e-10): boolean {
  const [a, b, c, d, e, f] = matrix;
  return (
    Math.abs(a - 1) < epsilon &&
    Math.abs(b) < epsilon &&
    Math.abs(c) < epsilon &&
    Math.abs(d - 1) < epsilon &&
    Math.abs(e) < epsilon &&
    Math.abs(f) < epsilon
  );
}

/**
 * Clones a matrix
 * @param matrix - Matrix to clone
 * @returns New matrix
 */
export function cloneMatrix(matrix: Matrix2D): Matrix2D {
  return [...matrix];
}

/**
 * Calculates the object dimensions after applying a transformation matrix
 * Used to compute selection frames
 */
export function sizeAfterTransform(width: number, height: number, transform: Matrix2D): Point {
  const halfW = width / 2;
  const halfH = height / 2;
  const points = [
    new Point(-halfW, -halfH),
    new Point(halfW, -halfH),
    new Point(halfW, halfH),
    new Point(-halfW, halfH),
  ].map(pt => pt.transform(transform));

  const xs = points.map(pt => pt.x);
  const ys = points.map(pt => pt.y);

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return new Point(maxX - minX, maxY - minY);
}
